import React, { FormEvent, useEffect, useState, useCallback } from "react";
import { Button, Modal } from "react-bootstrap";
import { FolderPlus, FileText, Check } from "lucide-react";
import {
  checkDuplicateFolderNameAsync,
  createFolder,
} from "../../../../../../services/fileServices/UserDriverService";
import Loader from "../../../../loader/Loader";
import { CheckDuplicateFolderNameRequest } from "../../../../../../interfaces/userDrive/createUserFolderRequest";
import { useAuth } from "../../../../../../firebase/AuthProvider";
import "./FolderUploadModal.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";

// Simple debounce utility
function debounce<T extends (...args: any[]) => void>(fn: T, delay: number) {
  let timer: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

interface FileItem {
  id: string;
  name: string;
  size: string;
  timeAgo: string;
}

interface FolderUploadModalProps {
  showFolderModal: boolean;
  handleCloseFolderModal: () => void;
  currentFolderId?: number;
  token: string;
  refreshCurrentFolder: () => void;
  availableFiles?: FileItem[]; // Optional: pass files from parent
}

// Static demo files for when no files are provided
const DEMO_FILES: FileItem[] = [
  {
    id: "1",
    name: "Project Proposal.pdf",
    size: "2.6 MB",
    timeAgo: "2 hours ago",
  },
  {
    id: "2",
    name: "Budget Report.xlsx",
    size: "1.1 MB",
    timeAgo: "5 hours ago",
  },
  {
    id: "3",
    name: "Meeting Notes.docx",
    size: "240 KB",
    timeAgo: "Yesterday",
  },
  {
    id: "4",
    name: "Presentation.pptx",
    size: "5.2 MB",
    timeAgo: "2 days ago",
  },
  {
    id: "5",
    name: "Contract.pdf",
    size: "890 KB",
    timeAgo: "3 days ago",
  },
];

const FolderUploadModal = ({
  showFolderModal,
  handleCloseFolderModal,
  token,
  currentFolderId,
  refreshCurrentFolder,
  availableFiles,
}: FolderUploadModalProps) => {
  const { currentUser } = useAuth();
  const [folderName, setFolderName] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [validated, setValidated] = useState<boolean>(false);

  const [isDuplicateName, setIsDuplicateName] = useState<boolean>(false);
  const [suggestedName, setSuggestedName] = useState<string>("");

  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);

  // Use provided files or demo files
  const files = availableFiles || DEMO_FILES;

  // Debounced duplicate check
  const debouncedCheck = useCallback(
    debounce(async (name: string) => {
      if (!name || !currentUser) return;

      const payload: CheckDuplicateFolderNameRequest = {
        userId: currentUser.uid,
        name: name,
        parentFolderID: currentFolderId || null,
        isFolder: true,
      };

      try {
        const res = await checkDuplicateFolderNameAsync(token, payload);
        if (res.success) {
          if (res.data.isDuplicate) {
            setIsDuplicateName(true);
            setSuggestedName(res.data.suggestedName);
          } else {
            setIsDuplicateName(false);
            setSuggestedName("");
          }
        }
      } catch (err) {
        console.error("Check duplicate failed", err);
      }
    }, 500),
    [currentUser, token, currentFolderId]
  );

  // Run check whenever name changes
  useEffect(() => {
    debouncedCheck(folderName);
  }, [folderName, debouncedCheck]);

  // Handle Tab press → apply suggestion
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Tab" && isDuplicateName && suggestedName) {
      e.preventDefault();
      setFolderName(suggestedName);
      setIsDuplicateName(false);
    }
  };

  // Toggle file selection
  const toggleFileSelection = (fileId: string) => {
    setSelectedFiles((prev) =>
      prev.includes(fileId)
        ? prev.filter((id) => id !== fileId)
        : [...prev, fileId]
    );
  };

  // Toggle select all
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedFiles([]);
      setSelectAll(false);
    } else {
      setSelectedFiles(files.map((f) => f.id));
      setSelectAll(true);
    }
  };

  // Update select all state when individual files change
  useEffect(() => {
    setSelectAll(selectedFiles.length === files.length && files.length > 0);
  }, [selectedFiles, files.length]);

  const handleCreateFolder = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!folderName.trim()) {
      setValidated(true);
      return;
    }

    if (isDuplicateName) return;

    try {
      setLoading(true);
      // Convert selected file IDs to numbers and filter out invalid IDs
      // Always send as array (empty array if no files selected)
      const fileIds = selectedFiles
        .map(id => Number(id))
        .filter(id => !isNaN(id));
      
      const response = await createFolder(token, {
        name: folderName,
        parentFolderId: currentFolderId,
        fileIds: fileIds, // Always send as array
      });

      setLoading(false);
      setFolderName("");
      setSelectedFiles([]);
      setSelectAll(false);
      refreshCurrentFolder();
      handleCloseFolderModal();
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setFolderName("");
    setSelectedFiles([]);
    setSelectAll(false);
    setValidated(false);
    setIsDuplicateName(false);
    setSuggestedName("");
    handleCloseFolderModal();
  };

  return (
    <>
      {loading && <Loader />}
      <Modal
        show={showFolderModal}
        onHide={handleModalClose}
        className="create-folder-popup"
        centered
        backdrop="static"
        keyboard={false}
      >
        <form noValidate onSubmit={handleCreateFolder}>
          <div className="modal-activity-reject-header">
            <h2 className="modal-activity-reject-title">Create Folder</h2>
            <button
              className="modal-activity-reject-close"
              onClick={(e) => {
                handleModalClose();
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <Modal.Body>
            {/* Folder Name Input */}
            <div className="folder-name-group">
              <label className="folder-name-label" htmlFor="folderName">
                Folder Name
              </label>
              <input
                id="folderName"
                required
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                onKeyDown={handleKeyDown}
                type="text"
                placeholder="Enter folder name..."
                className={`folder-name-input ${
                  isDuplicateName || (validated && !folderName.trim())
                    ? "is-invalid"
                    : ""
                }`}
              />
              {isDuplicateName && (
                <div className="invalid-feedback">
                  This name already exists. Try "{suggestedName}". Press TAB to
                  accept suggestion.
                </div>
              )}
              {validated && !folderName.trim() && !isDuplicateName && (
                <div className="invalid-feedback">
                  Please enter a valid folder name
                </div>
              )}
            </div>

            {/* Files Section */}
            {/* <div className="files-section">
              <div className="files-header">
                <h6 className="files-title">Select files to add (optional)</h6>
                <button
                  type="button"
                  className="select-all-btn"
                  onClick={handleSelectAll}
                >
                  {selectAll ? "Deselect All" : "Select All"}
                </button>
              </div>

              {files.length > 0 ? (
                <div className="files-list">
                  {files.map((file) => (
                    <div
                      key={file.id}
                      className={`file-list-item ${
                        selectedFiles.includes(file.id) ? "selected" : ""
                      }`}
                      onClick={() => toggleFileSelection(file.id)}
                    >
                      <div className="file-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedFiles.includes(file.id)}
                          onChange={() => toggleFileSelection(file.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="checkbox-custom">
                          <Check />
                        </span>
                      </div>

                      <div className="file-list-icon">
                        <FileText />
                      </div>

                      <div className="file-details">
                        <p className="file-details-name">{file.name}</p>
                        <p className="file-details-meta">
                          {file.size} • {file.timeAgo}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="files-empty">No files available</div>
              )}
            </div> */}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={handleModalClose}
              type="button"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              className="submit-btn "
              disabled={isDuplicateName || loading}
            >
              <FolderPlus />
              Create Folder
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </>
  );
};

export default FolderUploadModal;
